<script lang="ts">
  import { onDestroy } from 'svelte';
  import '$ui/styles/index.css';
  import { t, type TranslationKey } from '$core/i18n';
  import { PreferencesStore } from '$core/preferences';
  import {
    SessionStore,
    attachKeyboard,
    type SessionAction
  } from '$features/session';
  import CronometroCiego from '$ui/components/CronometroCiego.svelte';
  import ReviewItem from '$ui/components/ReviewItem.svelte';
  import SessionSidebar from '$ui/components/SessionSidebar.svelte';
  import PomodoroPanel from '$ui/components/PomodoroPanel.svelte';
  import RecentProblems from '$ui/components/RecentProblems.svelte';
  import SesionPreparacion from '$ui/components/SesionPreparacion.svelte';



  import SumarioSesion from '$ui/components/SumarioSesion.svelte';
  import Preferences from '$ui/components/Preferences.svelte';
  import ForecastView from '$ui/components/ForecastView.svelte';
  import SleepPanel from '$ui/components/SleepPanel.svelte';
  import GoalsPanel from '$ui/components/GoalsPanel.svelte';
  import ChecklistPanel from '$ui/components/ChecklistPanel.svelte';
  import ConjecturesPanel from '$ui/components/ConjecturesPanel.svelte';
  import HistoryPanel from '$ui/components/HistoryPanel.svelte';
  import AnalyticsPanel from '$ui/components/AnalyticsPanel.svelte';

  import AuthPanel from '$ui/components/AuthPanel.svelte';

  import MigrationPanel from '$ui/components/MigrationPanel.svelte';
  import OrphanSessionModal from '$ui/components/OrphanSessionModal.svelte';
  import QuarantinePanel from '$ui/components/QuarantinePanel.svelte';
  import LeaderboardPanel from '$ui/components/LeaderboardPanel.svelte';
  import FriendsPanel from '$ui/components/FriendsPanel.svelte';
  import GroupsPanel from '$ui/components/GroupsPanel.svelte';
  import { socialStore } from '$features/social';
  import { Toast } from '$ui/primitives/Toast';
  import SkipLink from '$ui/components/SkipLink.svelte';
  import Sidebar, { type ShellView } from '$ui/components/Sidebar.svelte';
  import Topbar from '$ui/components/Topbar.svelte';
  import BottomNav, { type BottomView } from '$ui/components/BottomNav.svelte';
  import Drawer from '$ui/components/Drawer.svelte';
  import { heartbeatLeaderboard } from '$features/leaderboard';
  import { quarantineStore } from '$features/quarantine';
  import { getQuarantineStats } from '$db/local/repositories/quarantine.repo';
  import { liveSync } from '$db/local/live.svelte';
  import { getOpenMacroSession, updateMacroSession } from '$db/local/repositories';
  import { identity } from '$core/identity';
  import { syncStore, attachOnlineSync } from '$features/sync';
  import { createCrossTabStore } from '$features/cross-tab';
  import {
    detectPendingMigration,
    type DetectionResult
  } from '$db/local/sync/migration';

  const store = new SessionStore();
  const prefs = new PreferencesStore();
  const crossTab = createCrossTabStore();

  store.setPrefsStore(prefs);

  // Sincronizar la preferencia de sonido con el motor de sesión
  $effect(() => {
    store.soundEnabled = prefs.prefs.alarmEnabled ?? true;
  });

  onDestroy(() => crossTab.destroy());

  // HMR safety: without this, Vite replaces App.svelte and leaves the old
  // CrossTabStore alive (holding the Web Lock and emitting broadcasts).
  // Multiple zombie stores cause a broadcast storm and high CPU.
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      crossTab.destroy();
    });
  }

  if (import.meta.env.DEV) {
    (window as unknown as { __crossTab?: typeof crossTab }).__crossTab = crossTab;
  }

  quarantineStore.load();
  liveSync(
    () => getQuarantineStats(),
    (stats) => quarantineStore.update(stats)
  );

  function openQuarantine(): void {
    quarantineStore.acknowledge();
    activeView = 'quarantine';
  }

  let activeView = $state<ShellView>('session');
  let showAuth = $state(false);

  let showPreferences = $derived(activeView === 'preferences');
  let showForecast = $derived(activeView === 'forecast');
  let showSleep = $derived(activeView === 'sleep');
  let showGoals = $derived(activeView === 'goals');
  let showChecklist = $derived(activeView === 'checklist');
  let showConjectures = $derived(activeView === 'conjectures');
  let showHistory = $derived(activeView === 'history');
  let showAnalytics = $derived(activeView === 'analytics');
  let showQuarantine = $derived(activeView === 'quarantine');
  let showLeaderboard = $derived(activeView === 'leaderboard');
  let showFriends = $derived(activeView === 'friends');
  let showGroups = $derived(activeView === 'groups');

  function navigateTo(view: ShellView) {
    activeView = view;
  }

  // Analytics emite `analytics:navigate-session` cuando el usuario clickea
  // "Ir a repasar" en el backlog. Acá lo convertimos en un cambio de vista.
  if (typeof window !== 'undefined') {
    window.addEventListener('analytics:navigate-session', () => {
      activeView = 'session';
    });
  }

  const VIEW_TITLES: Record<ShellView, TranslationKey> = {
    session: 'shell.nav.session',
    history: 'shell.nav.history',
    analytics: 'shell.nav.analytics',
    forecast: 'shell.nav.forecast',
    checklist: 'shell.nav.checklist',
    leaderboard: 'shell.nav.leaderboard',
    friends: 'shell.nav.friends',
    groups: 'shell.nav.groups',
    goals: 'shell.nav.goals',
    conjectures: 'shell.nav.conjectures',
    sleep: 'shell.nav.sleep',
    preferences: 'shell.nav.preferences',
    quarantine: 'shell.nav.quarantine'
  };

  let viewTitle = $derived(t(VIEW_TITLES[activeView]));
  let drawerOpen = $state(false);

  function handleBottomNav(view: BottomView) {
    if (view === 'more') {
      drawerOpen = true;
      return;
    }
    activeView = view;
  }

  function handleDrawerNavigate(view: ShellView) {
    activeView = view;
    drawerOpen = false;
  }
  let migration = $state<DetectionResult | null>(null);
  let takeoverMessage = $state<string | null>(null);
  let requestingTakeover = $state(false);
  let orphanStartedAt = $state<string | null>(null);
  let orphanSessionId = $state<string | null>(null);
  let wasMaster = $state(false);

  async function checkOrphanSession(): Promise<void> {
    const state = store.macro.state;
    const busy =
      state === 'focus_running' ||
      state === 'focus_paused' ||
      state === 'break_running' ||
      state === 'break_paused' ||
      state === 'pending_finish';
    if (busy) return;
    const open = await getOpenMacroSession();
    if (!open) return;
    if (open.id === store.macroSessionId) return;
    orphanSessionId = open.id;
    orphanStartedAt = open.startedAt;
  }

  async function resumeOrphan(): Promise<void> {
    orphanSessionId = null;
    orphanStartedAt = null;
    await store.reconstructFromDb();
  }

  async function discardOrphan(): Promise<void> {
    const id = orphanSessionId;
    orphanSessionId = null;
    orphanStartedAt = null;
    if (!id) return;
    await updateMacroSession(id, { status: 'discarded' });
  }

  $effect(() => {
    const isMaster = crossTab.isMaster;
    if (isMaster && !wasMaster) {
      void checkOrphanSession();
    }
    wasMaster = isMaster;
  });

  let showPrepare = $derived(
    (store.mode === 'A' || store.mode === 'B') &&
      (store.macro.state === 'inactive' ||
        store.macro.state === 'finished' ||
        store.macro.state === 'discarded')
  );

  // En sesión activa (pomodoro corriendo o problema en curso) ocultamos la
  // sidebar para entrar en modo foco — misma lógica que la app vanilla.
  let isSessionActive = $derived(
    !showPrepare && (activeView === 'session' || !activeView)
  );

  // Review mode (antes ModoB) vive embebido en SesionPreparacion (card
  // izquierdo). El swap fullscreen quedó retirado — Pomodoro siempre visible.

  function handleAction(action: SessionAction) {
    if (!crossTab.isMaster && action.startsWith('macro.')) {
      return;
    }
    switch (action) {
      case 'problem.toggle':
        store.toggleProblem();
        break;
      case 'problem.next':
        if (store.mode === 'B') {
          void store.commitCurrentRecall();
        } else {
          store.nextProblem();
        }
        break;
      case 'problem.discard':
        if (store.mode === 'B') {
          if (store.selectedGroupKey !== null) {
            void store.exitToGroups();
          } else {
            store.exitModeB();
          }
        } else {
          store.discardProblem();
        }
        break;
      case 'problem.rate1':
        if (store.problem.state === 'evaluating') {
          if (store.mode === 'B') {
            store.rateCurrentRecallOutcome('good');
          } else {
            store.rateProblem('correct');
          }
        }
        break;
      case 'problem.rate2':
        if (store.problem.state === 'evaluating') {
          if (store.mode === 'B') {
            store.rateCurrentRecallOutcome('again');
          } else {
            store.rateProblem('incorrect');
          }
        }
        break;
      case 'problem.rate3':
        if (store.problem.state === 'evaluating' && store.mode === 'A') {
          store.rateProblem('unresolved');
        }
        break;
      case 'macro.start':
        // El atajo de teclado debe usar el lastContext guardado — sin esto
        // la sesión arrancaba sin `macroSessionId` y los attempts quedaban
        // huérfanos. Si no hay contexto, avisamos en lugar de arrancar mudo.
        if (prefs.prefs.lastContext) {
          store.startSession(prefs.prefs.lastContext);
        } else {
          store.lastMessage =
            'Configura tu sesión (Subject → Section) antes de empezar.';
        }
        break;
      case 'macro.pause':
        store.pauseSession();
        break;
      case 'macro.resume':
        store.resumeSession();
        break;
      case 'macro.finish':
        store.finishSession();
        break;
    }
  }

  let overlayOpen = $derived(
    showAuth ||
      showPreferences ||
      showForecast ||
      showSleep ||
      showGoals ||
      showChecklist ||
      showConjectures ||
      showHistory ||
      showAnalytics ||
      showQuarantine ||
      showLeaderboard ||
      showFriends ||
      showGroups ||
      store.macro.state === 'pending_finish'
  );

  // Reloj maestro para el Pomodoro (MacroTimer).
  // Cada segundo revisa si el tiempo de Focus terminó para arrancar el Break.
  $effect(() => {
    const interval = window.setInterval(() => {
      store.tick();
    }, 1000);
    return () => window.clearInterval(interval);
  });

  // Temporizador de inactividad para cambios de contexto.
  // Si el usuario cambia de nodo y no presiona "Guardar" en 60s,
  // el sistema lo hace automáticamente para no perder datos.
  $effect(() => {
    const nodeId = store.context?.nodeId;
    const isRunning = store.macro.state === 'focus_running';
    const hasPendingChange = nodeId && nodeId !== store.activeNodeId;

    if (!isRunning || !hasPendingChange) return;

    const timer = window.setTimeout(() => {
      store.commitContextChange();
    }, 60_000);

    return () => window.clearTimeout(timer);
  });

  // --- Screen Wake Lock (Mantener pantalla encendida) ---
  let wakeLock: any = null;
  let wakeLockPending = false;

  async function requestWakeLock() {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    if (wakeLock !== null || wakeLockPending) return;

    // Chrome requiere que la página esté visible Y enfocada. Sin hasFocus(),
    // el navegador rechaza el request con NotAllowedError en bucle.
    if (typeof document !== 'undefined') {
      if (document.visibilityState !== 'visible') return;
      if (!document.hasFocus()) return;
    }

    wakeLockPending = true;
    try {
      wakeLock = await (navigator as any).wakeLock.request('screen');
      wakeLock.addEventListener('release', () => {
        wakeLock = null;
      });
    } catch {
      // NotAllowedError, AbortError, etc.: todos son esperados cuando la
      // pestaña pierde foco o el navegador aplica restricciones de energía.
      // No ensuciamos la consola.
    } finally {
      wakeLockPending = false;
    }
  }

  function releaseWakeLock() {
    if (wakeLock !== null) {
      wakeLock.release().then(() => {
        wakeLock = null;
      }).catch(() => {
        // Silencioso: el release puede fallar si el lock ya fue liberado
        // automáticamente por el navegador.
      });
    }
  }

  $effect(() => {
    const isTimerRunning = store.macro.state === 'focus_running' || store.macro.state === 'break_running';
    if (prefs.prefs.keepScreenAwake && isTimerRunning) {
      void requestWakeLock();
    } else {
      releaseWakeLock();
    }
    return () => releaseWakeLock();
  });

  // El navegador libera el Wake Lock automáticamente cuando cambias de pestaña.
  // Este effect lo vuelve a pedir cuando regresas a la app si el timer sigue corriendo.
  $effect(() => {
    if (typeof document === 'undefined') return;
    const handleVisibility = () => {
      const isTimerRunning = store.macro.state === 'focus_running' || store.macro.state === 'break_running';
      if (document.visibilityState === 'visible' && prefs.prefs.keepScreenAwake && isTimerRunning) {
        void requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  });

  $effect(() => {
    if (overlayOpen) return;
    const detach = attachKeyboard({
      shortcuts: prefs.prefs.shortcuts,
      onAction: handleAction
    });
    return detach;
  });

  $effect(() => {
    attachOnlineSync();
  });

  $effect(() => {
    const kind = identity.identity.kind;
    if (kind !== 'authenticated') return;
    void syncStore.bootstrap();
  });

  // Heartbeat de online status. Cada 5 min actualiza last_seen_at en
  // leaderboard_entries si el usuario está autenticado y tiene opt-in.
  // Sin esto, el indicador online del leaderboard siempre muestra offline
  // para usuarios inactivos aunque estén con la pestaña abierta.
  $effect(() => {
    const kind = identity.identity.kind;
    if (kind !== 'authenticated') return;
    const intervalId = window.setInterval(() => {
      // Guard: no hacer heartbeat si estamos offline.
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      void heartbeatLeaderboard();
    }, 5 * 60 * 1000);
    return () => window.clearInterval(intervalId);
  });

  // Polling social cada 60s para el badge de solicitudes pendientes.
  // Sin esto, el badge solo se actualiza al abrir el panel Amigos.
  // Guard: no intentar refrescar si el navegador está offline, para no
  // llenar la consola de errores ERR_FAILED / ERR_NAME_NOT_RESOLVED.
  $effect(() => {
    const kind = identity.identity.kind;
    if (kind !== 'authenticated') return;
    const intervalId = window.setInterval(() => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      void socialStore.refreshAll(true);
    }, 60 * 1000);
    return () => window.clearInterval(intervalId);
  });

  // Cuando cambia el userId, invalida la cache de retention del SessionStore.
  // Sin esto, el usuario B heredaría retention targets del usuario A.
  let lastSeenUserId = $state<string | null>(null);
  $effect(() => {
    const uid = identity.identity.userId;
    if (uid && uid !== lastSeenUserId) {
      if (lastSeenUserId !== null) {
        store.invalidateRetentionCache();
        socialStore.clear();
      }
      lastSeenUserId = uid;
      // Guard: no intentar refrescar social si estamos offline.
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        void socialStore.refreshAll(true);
      }
    }
  });

  $effect(() => {
    const id = identity.identity;
    if (id.kind !== 'authenticated') return;
    let cancelled = false;
    void (async () => {
      const result = await detectPendingMigration(id.userId);
      if (cancelled) return;
      if (result.shouldPrompt) {
        migration = result;
      }
    })();
    return () => {
      cancelled = true;
    };
  });

  // --- Cross-tab coordination ---

  let isFollower = $derived(!crossTab.isMaster);

  // Broadcast identity changes.
  $effect(() => {
    const kind = identity.identity.kind;
    const userId = identity.identity.userId;
    crossTab.emit('identity:changed', { kind, userId });
  });

  // Broadcast session lifecycle (only when this tab owns the session).
  $effect(() => {
    const state = store.macro.state;
    const sessionId = store.macroSessionId;
    if (state === 'focus_running' && sessionId && crossTab.isMaster) {
      crossTab.emit('session:started', {
        macroSessionId: sessionId,
        nodeId: store.context?.nodeId ?? null
      });
    } else if (state === 'finished' && sessionId && crossTab.isMaster) {
      crossTab.emit('session:finished', { macroSessionId: sessionId });
    } else if (state === 'discarded' && crossTab.isMaster) {
      crossTab.emit('session:discarded', { macroSessionId: sessionId });
    }
  });

  // Handle sync requests coming from followers.
  $effect(() => {
    const off = crossTab.on('sync:requested', () => {
      if (crossTab.isMaster && syncStore.canSync) {
        void syncStore.syncNow();
      }
    });
    return off;
  });

  // Refresh identity when another tab reports a change.
  $effect(() => {
    const off = crossTab.on('identity:changed', () => {
      void identity.refresh();
    });
    return off;
  });

  // Master handles takeover requests: releases lock only if no active session.
  $effect(() => {
    const off = crossTab.on('master:takeover-request', () => {
      if (!crossTab.isMaster) return;
      const state = store.macro.state;
      const sessionActive =
        state === 'focus_running' ||
        state === 'focus_paused' ||
        state === 'break_running' ||
        state === 'break_paused' ||
        state === 'pending_finish';
      if (sessionActive) {
        crossTab.denyTakeover('session_active');
        return;
      }
      crossTab.releaseMasterLock();
    });
    return off;
  });

  // Follower reacts to takeover resolution.
  $effect(() => {
    const offDenied = crossTab.on('master:takeover-denied', (payload) => {
      requestingTakeover = false;
      const reason = (payload as { reason?: string } | null)?.reason;
      if (reason === 'session_active') {
        takeoverMessage = t('crossTab.masterBusy');
      } else {
        takeoverMessage = t('crossTab.masterBusy');
      }
      window.setTimeout(() => {
        takeoverMessage = null;
      }, 4000);
    });
    return offDenied;
  });

  $effect(() => {
    const offGranted = crossTab.on('master:takeover-granted', () => {
      requestingTakeover = false;
    });
    return offGranted;
  });

  // When this tab becomes master (post-takeover), clear any pending state.
  $effect(() => {
    if (crossTab.isMaster) {
      requestingTakeover = false;
    }
  });

  function requestTakeover(): void {
    if (crossTab.isMaster) return;
    requestingTakeover = true;
    crossTab.requestTakeover();
    window.setTimeout(() => {
      requestingTakeover = false;
    }, 5000);
  }
</script>

<main class:focus-mode={store.isProblemRunning}>
  <SkipLink />

  <div class="shell" class:hide-sidebar={isSessionActive}>
    <div class="sidebar-desktop">
      <Sidebar
        active={activeView}
        onNavigate={navigateTo}
        user={{
          name:
            identity.identity.kind === 'authenticated'
              ? t('shell.user.authenticated')
              : t('shell.user.guest'),
          avatarUrl: null,
          syncLabel: t('shell.sync.idle'),
          syncVariant: 'idle'
        }}
        onUserClick={() => {
          showAuth = true;
        }}
        quarantineCount={quarantineStore.count}
        pendingFriendsCount={socialStore.pendingCount}
      />
    </div>
    <div class="main-area">
      <Topbar 
        title={viewTitle} 
        onOpenMenu={() => (drawerOpen = true)} 
        hideUser={store.macro.state !== 'inactive'}
        user={{
          name: identity.identity.kind === 'authenticated' 
            ? (identity.identity.displayName || identity.identity.email?.split('@')[0] || 'Account') 
            : t('shell.user.guest'),
          avatarUrl: null
        }} 
      />
      <div class="content">

  {#if isFollower}
    <div class="follower-banner" role="status">
      <span>{t('crossTab.followerBanner')}</span>
      <button
        class="takeover-button"
        disabled={requestingTakeover}
        onclick={requestTakeover}
      >
        {requestingTakeover ? t('crossTab.takingOver') : t('crossTab.takeOver')}
      </button>
    </div>
  {/if}

  {#if quarantineStore.hasNew}
    <div class="quarantine-banner" role="status">
      <span>
        {t('quarantine.bannerNew', { n: String(quarantineStore.count) })}
      </span>
      <button class="quarantine-banner-action" onclick={openQuarantine}>
        {t('quarantine.bannerReview')}
      </button>
    </div>
  {/if}

  {#if takeoverMessage}
    <div class="notice notice-warning" role="status">
      {takeoverMessage}
    </div>
  {/if}



  {#if activeView === 'forecast'}
    <ForecastView {store} />
  {:else if activeView === 'session' || !activeView}
    {#if showPrepare}
      <SesionPreparacion
        {store}
        {prefs}
        disabled={isFollower}
        onNavigate={(view) => (activeView = view)}
      />
    {:else}
      <div class="session-3col">
        <div class="col-start">
          <PomodoroPanel {store} {prefs} />
          <SessionSidebar {store} />
        </div>
        <div class="col-center">
          {#if store.mode === 'B'}
            <ReviewItem {store} {prefs} />
          {:else}
            <CronometroCiego {store} {prefs} />
          {/if}
        </div>
        <div class="col-end">
          <RecentProblems {store} />
        </div>
      </div>
    {/if}
  {/if}

  {#if showPreferences}
    <Preferences {prefs} />
  {/if}

  {#if showSleep}
    <SleepPanel />
  {/if}

  {#if showGoals}
    <GoalsPanel />
  {/if}

  {#if showChecklist}
    <ChecklistPanel {prefs} />
  {/if}

  {#if showConjectures}
    <ConjecturesPanel />
  {/if}

  {#if showHistory}
    <HistoryPanel />
  {/if}

  {#if showAnalytics}
    <AnalyticsPanel />
  {/if}

  {#if showQuarantine}
    <QuarantinePanel />
  {/if}

  {#if showLeaderboard}
    <LeaderboardPanel />
  {/if}

  {#if showFriends}
    <FriendsPanel
      onOpenGroups={() => {
        activeView = 'groups';
      }}
    />
  {/if}

  {#if showGroups}
    <GroupsPanel />
  {/if}

  {#if store.macro.state === 'pending_finish'}
    <SumarioSesion {store} {prefs} />
  {/if}

  {#if showAuth}
    <AuthPanel onClose={() => (showAuth = false)} />
  {/if}

  {#if orphanStartedAt}
    <OrphanSessionModal
      startedAt={orphanStartedAt}
      onResume={resumeOrphan}
      onDiscard={discardOrphan}
    />
  {/if}

  {#if migration && migration.shouldPrompt && migration.guestCounts && migration.stats && migration.plan && migration.guestId}
    <MigrationPanel onClose={() => (migration = null)} />
  {/if}
      </div>
      <BottomNav active={activeView} onNavigate={handleBottomNav} />
    </div>
    <Drawer bind:open={drawerOpen} onClose={() => (drawerOpen = false)}>
      <div class="drawer-content">
        <Sidebar
          initialCollapsed={false}
          active={activeView}
          onNavigate={handleDrawerNavigate}
          user={{
            name:
              identity.identity.kind === 'authenticated'
                ? t('shell.user.authenticated')
                : t('shell.user.guest'),
            avatarUrl: null,
            syncLabel: t('shell.sync.idle'),
            syncVariant: 'idle'
          }}
          onUserClick={() => {
            drawerOpen = false;
            showAuth = true;
          }}
          quarantineCount={quarantineStore.count}
          pendingFriendsCount={socialStore.pendingCount}
        />
      </div>
    </Drawer>
  </div>
</main>

<Toast />

<style>
  main {
    font-family: var(--font-sans);
    color: var(--color-text-primary);
    background: var(--color-bg);
    min-height: 100dvh;
  }

  .shell {
    display: flex;
    min-height: 100dvh;
  }

  .sidebar-desktop {
    display: flex;
    flex: 0 0 auto;
  }

  .shell.hide-sidebar .sidebar-desktop {
    display: none;
  }

  /* Modo focus durante un problema (F6 §8 Ockham, F6 §39 minimalismo).
     Atenuamos la sidebar para reducir competencia visual con el timer —
     el usuario está en deep work y las acciones laterales son ruido. */
  main.focus-mode .sidebar-desktop {
    opacity: 0.4;
    transition: opacity var(--dur-base) var(--ease-out);
  }

  main.focus-mode .sidebar-desktop:hover,
  main.focus-mode .sidebar-desktop:focus-within {
    opacity: 1;
  }

  @media (max-width: 767px) {
    .sidebar-desktop {
      display: none;
    }
  }

  .main-area {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
  }

  .drawer-content {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  .drawer-content :global(.sidebar) {
    width: 100%;
    position: static;
    height: auto;
    border-right: none;
    background: transparent;
  }

  @media (min-width: 768px) {
    :global(.topbar),
    :global(.bottom-nav) {
      display: none !important;
    }
  }

  .content {
    flex: 1;
    min-width: 0;
    padding: var(--space-8) var(--space-8);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }



  .shell.hide-sidebar .content {
    /* Mantenemos simetría para no descolocar el layout central */
    padding: var(--space-4);
  }

  @media (max-width: 1024px) {
    .content {
      /* Añadimos padding-bottom para que la barra inferior no tape el contenido */
      padding: var(--space-6) var(--space-4) 120px var(--space-4);
    }
  }



  .notice {
    padding: var(--space-3) var(--space-4);
    background: var(--color-surface);
    border: 1px solid var(--color-warning);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-size: var(--text-sm);
  }

  .follower-banner {
    padding: var(--space-2) var(--space-4);
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-border);
    border-left: 3px solid var(--color-accent);
    border-radius: var(--radius-md);
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }

  .takeover-button {
    flex: 0 0 auto;
    padding: var(--space-1) var(--space-3);
    font-size: var(--text-caption);
    font-family: inherit;
    font-weight: var(--weight-medium);
    cursor: pointer;
    color: var(--color-accent);
    background: transparent;
    border: 1px solid var(--color-accent);
    border-radius: var(--radius-sm);
  }

  .takeover-button:hover:not(:disabled) {
    color: var(--color-accent-fg);
    background: var(--color-accent);
  }

  .takeover-button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .notice-warning {
    border-color: var(--color-danger);
    color: var(--color-danger);
  }

  .quarantine-banner {
    padding: var(--space-2) var(--space-4);
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-warning);
    border-left: 3px solid var(--color-warning);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-size: var(--text-sm);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }

  .quarantine-banner-action {
    flex: 0 0 auto;
    padding: var(--space-1) var(--space-3);
    font-size: var(--text-caption);
    font-family: inherit;
    font-weight: var(--weight-medium);
    cursor: pointer;
    color: var(--color-warning);
    background: transparent;
    border: 1px solid var(--color-warning);
    border-radius: var(--radius-sm);
  }

  .quarantine-banner-action:hover {
    color: var(--color-accent-fg);
    background: var(--color-warning);
  }

  /* Layout de la sesión activa: 3 columnas simétricas.
     Izquierda: RecentProblems (historial de la cadena activa).
     Centro: timer del problema + input de conjetura.
     Derecha: 6 campos de setup + pomodoro con círculo + controles.
     Ambas sidebars 100% alto con scroll interno, sin scroll de página. */
  .session-3col {
    display: grid;
    grid-template-columns: 320px 1fr 320px;
    gap: var(--space-4);
    width: 100%;
    height: calc(100dvh - var(--space-4) * 2);
    overflow: hidden;
    align-items: stretch;
  }

  /* Naming direccional neutro (F5 §13): start/end en lugar de left/right,
     para compatibilidad futura con layouts RTL sin renombrar. */
  .col-start,
  .col-end {
    min-width: 0;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    padding-right: 4px;
  }

  .col-center {
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  @media (max-width: 1024px) {
    .session-3col {
      grid-template-columns: 1fr;
      height: auto;
      overflow: visible;
    }
    .col-start,
    .col-end,
    .col-center {
      height: auto;
      overflow: visible;
    }
  }

  /* === GLOBAL VIEW HEADERS (UNIFIED ANALYTICS STYLE) === */
  :global(.view-title) {
    font-family: var(--font-display) !important;
    font-size: 1.35rem !important;
    font-weight: 700 !important;
    letter-spacing: -0.02em !important;
    line-height: 1.1 !important;
    margin: 0 !important;
    color: var(--color-text-primary) !important;
    text-wrap: balance;
  }

  :global(.view-subtitle) {
    font-family: var(--font-body, var(--font-sans)) !important;
    font-size: var(--text-sm) !important;
    color: var(--color-text-muted) !important;
    margin: 0 !important;
    margin-top: var(--space-1) !important; /* Separación exacta entre título y subtítulo */
  }
</style>